import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  CircularProgress,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useParams, useNavigate } from 'react-router-dom';
import { postData } from '../../Webservice/Webservice';
import { toast } from 'react-toastify';

function ForgotPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!password) {
      newErrors.password = 'New password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await postData(`customer/reset-password/${token}`, {
        newPassword: password,
      });
      if (response && !response.error) {
        toast.success('Password reset successfully!');
        setIsSuccess(true);
      } else {
        toast.error(response?.message || 'Failed to reset password. The link may have expired.');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Something went wrong. Please try again or request a new reset link.');
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&:hover fieldset': { borderColor: '#000' },
      '&.Mui-focused fieldset': { borderColor: '#000' },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: "url('/img/mailbanner.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        px: 2,
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: '100%',
          maxWidth: 400,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(255,255,255,0.95)',
          textAlign: 'center',
        }}
      >
        {/* Logo */}
        <img
          src="/img/tvfmlogo1.png"
          alt="TVFM logo"
          style={{ width: 160, marginBottom: 20, cursor: "pointer" }}
          onClick={() => navigate("/")}
        />

        {isSuccess ? (
          /* ── Success State ── */
          <Box>
            <CheckCircleOutlineIcon
              sx={{ fontSize: 56, color: '#2e7d32', mb: 1.5 }}
            />
            <Typography variant="h6" fontWeight={700} mb={1}>
              Password Reset Successfully!
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Your password has been updated. You can now sign in with your new
              password.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate('/signin')}
              sx={{
                py: 1.3,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                backgroundColor: '#C8A054',
                color: '#fff',
                '&:hover': { backgroundColor: '#b38841' },
              }}
            >
              Go to Sign In
            </Button>
          </Box>
        ) : (
          /* ── Reset Form ── */
          <Box>
            <Typography variant="h6" fontWeight={700} mb={0.5}>
              Reset Your Password
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Enter and confirm your new password below.
            </Typography>

            {/* New Password */}
            <TextField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (errors.password)
                  setErrors((prev) => ({ ...prev, password: '' }));
              }}
              error={!!errors.password}
              helperText={errors.password}
              sx={fieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Confirm Password */}
            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword)
                  setErrors((prev) => ({ ...prev, confirmPassword: '' }));
              }}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              sx={fieldStyle}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              fullWidth
              disabled={loading}
              onClick={handleSubmit}
              sx={{
                mt: 1,
                py: 1.3,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                backgroundColor: '#C8A054',
                color: '#fff',
                '&:hover': { backgroundColor: '#b38841' },
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: 'white' }} />
              ) : (
                'Reset Password'
              )}
            </Button>

            <Typography variant="body2" sx={{ mt: 2.5 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/signin')}
                underline="hover"
                sx={{ color: '#C8A054', fontWeight: 500 }}
              >
                ← Back to Sign In
              </Link>
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default ForgotPasswordPage;
